import { Eye, EyeOff, Info, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { client } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { UppercaseInput } from '@/components/ui/uppercase-input'
import { Label } from '@/components/ui/label'
import { useAuthStore } from '@/stores/auth'

interface PhoneData {
  number: string
  phone_type: string
}

interface RegisterForm {
  first_name: string
  last_name: string
  email: string
  phones: PhoneData[]
  password: string
  password_confirm: string
}

export function RegisterPage() {
  const { t } = useTranslation(['auth', 'profile'])
  const navigate = useNavigate()
  const { setToken, setParent } = useAuthStore()
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [phones, setPhones] = useState<PhoneData[]>([{ number: '', phone_type: 'mobile' }])

  const { register, handleSubmit, formState: { errors }, watch } = useForm<RegisterForm>()
  const password = watch('password', '')

  const addPhone = () => {
    if (phones.length < 5) {
      setPhones([...phones, { number: '', phone_type: 'home' }])
    } else {
      setError(t('validation.maxPhones', { ns: 'profile' }))
    }
  }

  const removePhone = (index: number) => {
    if (phones.length > 1) {
      setPhones(phones.filter((_, i) => i !== index))
    }
  }

  const updatePhoneType = (index: number, type: string) => {
    const newPhones = [...phones]
    newPhones[index].phone_type = type
    setPhones(newPhones)
  }

  const onSubmit = async (data: RegisterForm) => {
    try {
      setError('')
      setEmailError('')
      setPasswordError('')

      const validPhones = phones.filter(phone => phone.number.trim() !== '')

      if (validPhones.length === 0) {
        setError(t('register.phoneNumberRequired'))
        return
      }

      const hasMobilePhone = validPhones.some(phone => phone.phone_type === 'mobile')
      if (!hasMobilePhone) {
        setError(t('register.mobileRequired'))
        return
      }

      if (validPhones.length > 5) {
        setError(t('validation.maxPhones', { ns: 'profile' }))
        return
      }

      const apiData = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phones: validPhones,
        password: data.password,
        password_confirm: data.password_confirm,
        language_preference: localStorage.getItem('i18nextLng') || 'el'
      }

      const response = await client.post('/auth/register/', apiData)
      const { token, parent } = response.data
      setToken(token)
      setParent(parent)
      navigate('/verification-pending')
    } catch (err: any) {
      if (err.response?.data?.email &&
          Array.isArray(err.response.data.email) &&
          err.response.data.email.some((msg: string) => msg.includes('Parent with this Email Address already exists'))) {
        setEmailError(t('register.emailAlreadyExists'))
      } else if (err.response?.data?.password) {
        if (Array.isArray(err.response.data.password) &&
            err.response.data.password.some((msg: string) => msg.includes('This password is too common'))) {
          setPasswordError(t('register.passwordTooCommon'))
        } else {
          setError(Array.isArray(err.response.data.password) ? err.response.data.password[0] : err.response.data.password)
        }
      } else if (err.response?.data?.password_confirm) {
        setError(Array.isArray(err.response.data.password_confirm) ? err.response.data.password_confirm[0] : err.response.data.password_confirm)
      } else if (err.response?.data?.non_field_errors) {
        setError(Array.isArray(err.response.data.non_field_errors) ? err.response.data.non_field_errors[0] : err.response.data.non_field_errors)
      } else {
        setError(err.response?.data?.error || t('register.error'))
      }
    }
  }

  const isPhoneError = error === t('register.phoneNumberRequired') ||
                       error === t('register.mobileRequired') ||
                       error === t('validation.maxPhones', { ns: 'profile' })

  return (
    <AuthLayout>
      {/* Logo */}
      <div className="flex justify-center mb-8">
        <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
      </div>

      {/* Register Card */}
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('register.createAccount')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            {error && !isPhoneError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="first-name">
                {t('register.firstName')}
                <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
              </Label>
              <UppercaseInput
                id="first-name"
                {...register('first_name', { required: t('register.firstNameRequired') })}
                type="text"
              />
              {errors.first_name && <p className="text-xs text-destructive">{errors.first_name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="last-name">
                {t('register.lastName')}
                <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
              </Label>
              <UppercaseInput
                id="last-name"
                {...register('last_name', { required: t('register.lastNameRequired') })}
                type="text"
              />
              {errors.last_name && <p className="text-xs text-destructive">{errors.last_name.message}</p>}
              <div className="flex items-start gap-2 p-2 bg-muted rounded-md text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{t('register.nameRequirements')}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                {t('register.email')}
                <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
              </Label>
              <Input
                id="email"
                {...register('email', {
                  required: t('register.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('register.invalidEmail')
                  }
                })}
                type="email"
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>

            {/* Phone Numbers */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>
                  {t('register.phoneNumbers')}
                  <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </Label>
                <Button type="button" variant="ghost" size="sm" onClick={addPhone} disabled={phones.length >= 5}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  {t('register.addPhone')}
                </Button>
              </div>

              {phones.map((phone, index) => (
                <div key={index} className="flex gap-2">
                  <select
                    value={phone.phone_type}
                    onChange={(e) => updatePhoneType(index, e.target.value)}
                    className="flex h-9 w-1/3 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="mobile">{t('register.mobile')}</option>
                    <option value="home">{t('register.home')}</option>
                    <option value="work">{t('register.work')}</option>
                    <option value="other">{t('register.other')}</option>
                  </select>
                  <Input
                    type="tel"
                    value={phone.number}
                    onChange={(e) => {
                      const newPhones = [...phones]
                      newPhones[index].number = e.target.value
                      setPhones(newPhones)
                    }}
                    placeholder={t('register.phoneNumberPlaceholder')}
                    className="w-2/3"
                  />
                  {phones.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePhone(index)} className="flex-shrink-0">
                      <X className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
              {isPhoneError && <p className="text-xs text-destructive">{error}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('register.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  {...register('password', {
                    required: t('register.passwordRequired'),
                    minLength: { value: 8, message: t('register.passwordMinLength') },
                    pattern: { value: /^(?=.*[A-Za-z])(?=.*\d).+$/, message: t('register.passwordComplexity') }
                  })}
                  type={showPassword ? "text" : "password"}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
              {passwordError && <p className="text-xs text-destructive">{passwordError}</p>}
              <div className="p-2 bg-muted rounded-md text-xs text-muted-foreground">
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>{t('register.passwordMinLength')}</li>
                  <li>{t('register.passwordComplexity')}</li>
                  <li>{t('register.passwordNotCommon')}</li>
                  <li>{t('register.passwordLatinOnly')}</li>
                  <li>{t('register.passwordNoEmail')}</li>
                </ul>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password-confirm">{t('register.confirmPassword')}</Label>
              <Input
                id="password-confirm"
                {...register('password_confirm', {
                  required: t('register.confirmPasswordRequired'),
                  validate: value => value === password || t('register.passwordsDoNotMatch')
                })}
                type="password"
              />
              {errors.password_confirm && <p className="text-xs text-destructive">{errors.password_confirm.message}</p>}
            </div>

            <Button type="submit" className="w-full">
              {t('register.createAccount')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Login link */}
      <Card className="mt-4">
        <CardContent className="py-4 text-center text-sm">
          {t('register.hasAccount')}{' '}
          <Link to="/login" className="text-primary font-medium hover:underline">
            {t('register.loginLink')}
          </Link>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
